import { cn } from "@/utils/cn";
import { useEffect, useRef, useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import { LanguageLevel } from "../types/onboarding";

interface LevelOption {
  value: LanguageLevel;
  label: string;
  color: string;
}

export const LEVELS: LevelOption[] = [
  { value: "beginner", label: "Beginner", color: "bg-green-100 text-green-800" },
  { value: "intermediate", label: "Intermediate", color: "bg-blue-100 text-blue-800" },
  { value: "advanced", label: "Advanced", color: "bg-purple-100 text-purple-800" },
  { value: "fluent", label: "Fluent", color: "bg-indigo-100 text-indigo-800" },
];

interface LevelDropdownProps {
  value: LanguageLevel;
  onChange: (level: LanguageLevel) => void;
  className?: string;
}

export function LevelDropdown({ value, onChange, className = "" }: LevelDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedLevel = LEVELS.find((level) => level.value === value) || LEVELS[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between min-w-[140px] border border-gray-300 rounded-md p-2 bg-white"
      >
        <div className={`px-2 py-1 rounded-md text-xs font-medium ${selectedLevel.color}`}>{selectedLevel.label}</div>
        <FiChevronDown className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute flex flex-col z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg overflow-hidden">
          {LEVELS.map((level) => (
            <div
              key={level.value}
              onClick={() => {
                onChange(level.value);
                setIsOpen(false);
              }}
              className={`flex justify-start p-2 cursor-pointer hover:bg-gray-50 ${
                level.value === value ? "bg-gray-50" : ""
              }`}
            >
              <div className={`px-2 py-1 rounded-md text-xs font-medium ${level.color}`}>{level.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
